(ns leg.state
  "What the window is looking at, and every way it changes.

  One atom. The server is the truth - what is read, what is subscribed, how
  much of a feed is left - and this holds the last thing it said plus the
  two things that are nobody's business but this window's: which feed is
  open and which filter is on.

  Every call that changes something on the server answers with the feeds
  again, so the unread counts in the sidebar are never computed here and
  can never drift from what is actually stored. The only optimism in this
  file is marking an article read on the way into it: the row must go grey
  under the finger that pressed it, and the server says the same thing a
  moment later.

  One thing here is fetched before anybody asked for it, and only one: the
  article on either side of the one being read. A swipe is a movement that
  has already started when the request would go out, and a page that slid
  and then waited is two movements - so the answer is in hand before the
  finger is (`look-ahead!`). Nothing else is guessed at.

  What is open is also in the address bar - the feed or the group, the
  article, the page, and on a narrow screen which of the three panes the
  window is standing in - and this is the file that keeps the two in step. Moving into something
  writes the address; the back button writes the state. See `leg.route` for
  the shapes, and for why the filter is not a place and the pane is not a
  path."
  (:require [goog.object :as gobj]
            [kc.core :as kc]
            [leg.api :as api]
            [leg.auth :as auth]
            [leg.mui :as mui]
            [leg.route :as route]
            [leg.util :as u]
            [leg.vt :as vt]))

(defn- now-iso
  "Now, the way the server writes it. What is put in a row here before the
  server has answered has to look like what comes back, or the same article
  changes shape when the list is fetched again."
  []
  (.toISOString (js/Date.)))

(defonce !state
  (atom {:feeds     []
         :settings  {}
         :version   nil
         :feed      nil            ; nil is every feed at once
         :group     nil            ; a name is one group of them; nil is not in one
         :filter    :unread        ; :unread | :all | :starred
         :feed-filter :unread      ; :unread | :all - which names are listed
         :articles  []
         :article   nil            ; the one being read, body and all
         :pane      :articles      ; which pane a narrow window shows
         :page      nil            ; :settings | :admin - over the whole window
         :readers   nil            ; everybody, for the admin page
         :door      nil            ; whether the next new address may in
         :loading?  false
         :article-loading? false    ; one is on its way in; the old one stays
         :busy?     false          ; a fetch or an import is running
         :refreshing? false        ; and that one of them is the [Refresh] press
         :dialog    nil            ; [:feed id] | :add-feed | :add-reader | :mark-read
         :error     nil}))

(defn- fail! [e]
  (when-not (api/unauthorized? e)
    (swap! !state assoc :error (api/message-of e)))
  (swap! !state assoc :loading? false :busy? false :refreshing? false)
  e)

(defn- article-done! [] (swap! !state assoc :article-loading? false))

(defn dismiss-error! [] (swap! !state assoc :error nil))

(defn dialog!
  "Which form is laid over the window, or `nil` for none. Not an address: a
  dialog is a sentence said about the row that was pressed, and the page it
  was said on is still there behind it (see `leg.dialogs`)."
  [d]
  (swap! !state assoc :dialog d :error nil))

;;; ---------------------------------------------------------------- address

(defn- here
  "What to write in the address bar for this feed, filter, article and
  pane. The article is given as its id and not as the map, because the
  address is written before the article has been fetched."
  ([] (here nil))
  ([article-id]
   (let [{:keys [feed group filter feed-filter pane]} @!state]
     (route/href {:feed feed :group group :filter filter :feed-filter feed-filter
                  :article article-id :pane pane}))))

(defn- depth
  "How many addresses this window has written since it was opened. Kept in
  the history entry itself, because that is the only place that survives a
  back and a forward - and it is what tells a window that walked into an
  article from one that was opened on it."
  []
  (or (some-> js/history .-state (gobj/get "leser-depth")) 0))

(defn- go!
  "Into something: pushed, so the back button comes back out of it."
  [href]
  (.pushState js/history #js {"leser-depth" (inc (depth))} "" href))

(defn- stay!
  "Something changed that is not a place to come back out of - another
  filter, the next article in the same list. Replaced, so the back button
  still leads out of the list rather than through everything read in it."
  [href]
  (.replaceState js/history (or (.-state js/history) #js {}) "" href))

(defn open-feeds!
  "Out of the list of articles and onto the list of feeds, which is what a
  narrow window does instead of showing all three at once.

  A place, so it is pushed: a reload while it is open comes back to it, and
  the back button - the one in the browser and the phone's own - leads back
  to the list it was opened from instead of out of the reader. The feed
  that is open stays open and stays in the address; the window is standing
  one pane to the left of it.

  Drawn as a move backwards, because that is where the pane is: the three
  are side by side on a wide screen and the phone is looking at one of them
  at a time."
  []
  (let [{:keys [feed group filter feed-filter]} @!state]
    (go! (route/href {:feed feed :group group :filter filter
                      :feed-filter feed-filter :pane :feeds})))
  (vt/transition! :back :all #(swap! !state assoc :pane :feeds)))

;;; ------------------------------------------------------------------ admin

(declare load-readers!)

(defn open-page!
  "Into the settings or the admin page: a place, so it is pushed and the
  back button comes back out of it onto whatever was being read."
  [page]
  (go! (route/href {:page page}))
  (vt/transition! :forward :all #(swap! !state assoc :page page :dialog nil))
  (when (= :admin page) (load-readers!)))

(defn open-settings! [] (open-page! :settings))
(defn open-admin! [] (open-page! :admin))

(defn close-page!
  "Out again. The browser's own back where this window walked in, so the
  button in the corner and the one in the browser do the same thing."
  []
  (if (pos? (depth))
    (.back js/history)
    (do (vt/transition! :back :all #(swap! !state assoc :page nil :dialog nil))
        (stay! (here)))))

(defn- feeds! [answer]
  (when-let [fs (:feeds answer)]
    (swap! !state assoc :feeds fs))
  answer)

;;; ---------------------------------------------------------------- reading

(defn- query
  "Which list to ask for: one feed, one group, or everything. The two are
  never both set - going into either clears the other - so the server is
  never asked to decide which of them it meant."
  []
  (let [{:keys [feed group filter]} @!state]
    {:feed feed :group group
     :unread? (= :unread filter) :starred? (= :starred filter)}))

(declare look-ahead!)

(defn load-articles!
  "The list for whatever is selected. Its own flag rather than the busy
  one: a list that is being fetched should grey out, and the window around
  it should not."
  []
  (swap! !state assoc :loading? true)
  (-> (api/articles! (query))
      (.then (fn [{:keys [articles]}]
               (swap! !state assoc :articles articles :loading? false)
               ;; What is next to the open article is a question about the
               ;; list, so the answer changes when the list does - and a
               ;; window opened straight onto an article has no list at all
               ;; until this returns, which is the case this is really for.
               (look-ahead!)))
      (.catch fail!)))

(declare load-article!)

(defn- open-from-address!
  "What the address names and this window does not have yet: the article, or
  the readers when the address is the admin page. Checked here rather than
  when the address is first read, because a window may be opened straight
  onto either of them before it has a key to fetch them with - and then this
  runs again once it has one."
  []
  (let [{:keys [article page]} (route/parse (.-href js/location))]
    (when (and article (not= article (:article/id (:article @!state))))
      (load-article! article))
    (when (and (= :admin page) (not (:readers @!state)))
      (load-readers!))))

(defn load-state!
  "The sidebar and the settings, and then the list. Called on the way in
  and on a timer afterwards."
  []
  (-> (api/state!)
      (.then (fn [{:keys [feeds settings version]}]
               (swap! !state assoc :feeds feeds :settings settings :version version)))
      (.then load-articles!)
      (.then open-from-address!)
      (.catch fail!)))

(defn- start-pane!
  "Where a window that was opened on nothing in particular comes up: the
  articles of every feed, or the list of feeds (`:reader/start`, read by
  `leg.util/start-feeds?`).

  Three things have to be true, and the first two are what keep a setting
  about where reading starts from reaching anywhere else.

  The address must have named nothing. A feed, a group, an article or a
  page in it is somebody arriving somewhere - a link, a reload, the back
  button - and that is where they go, whatever they said about openings.
  Only the filters may be in it: what somebody reads with is not where
  they are.

  And the window must be narrow. On a screen with room for three panes
  the feeds are beside the articles, so both answers are the same screen -
  the same question `home!` is handed.

  The address is replaced and not pushed: this is where the window came
  up, not a move it made, and the way out of it is out of the reader."
  []
  (let [{:keys [feed group article page pane settings]} @!state]
    (when (and (nil? feed) (nil? group) (nil? article) (nil? page)
               (= :articles pane)
               (u/start-feeds? (:reader/start settings))
               (mui/one-pane?))
      (swap! !state assoc :pane :feeds)
      (stay! (here)))))

(defn start!
  "The way in, once: everything `load-state!` fetches, and then wherever
  this reader starts (`start-pane!`).

  A second function and not a line inside `load-state!`, because
  `load-state!` is called again - after an import, after a sign-in - and a
  reload of the feeds that moved somebody to another pane would be a
  window that wandered off while they were looking at it."
  []
  (.then (load-state!) (fn [_] (start-pane!))))

(defn poll!
  "The counts again, without touching the list. A reader is not a chat: a
  number in the sidebar every couple of minutes is the whole of what
  changes by itself, and a list that reshuffled under somebody reading it
  would be a bug."
  []
  (-> (api/state!)
      (.then (fn [{:keys [feeds settings]}]
               (swap! !state assoc :feeds feeds :settings settings)))
      (.catch (fn [_] nil))))

(defn- show-feed!
  "One feed, or every one of them. Either way out of a group: a feed is not
  inside one, it carries a name, and a window showing both would be showing
  a list it cannot describe."
  [id]
  (swap! !state assoc :feed id :group nil :article nil :articles [] :pane :articles)
  (load-articles!))

(defn select-feed!
  "Into a feed, or out to every feed at once. A place, so it is pushed and
  the back button leads back to the feed that was open before it.

  The address written is the one being gone to and not `here`: `here` is
  computed from the state, and the state is changed below this line. A push
  of the old address would leave the window showing one feed and the address
  bar naming another - and a reload would come back to the wrong one."
  [id]
  (go! (route/href (merge (select-keys @!state [:filter :feed-filter])
                          {:feed id})))
  ;; `:list` and not `:all`: the list changes and with it the article, but
  ;; the feeds beside them do not - see leg.vt.
  (vt/transition! :forward :list #(do (swap! !state assoc :feed id :article nil)
                                      (show-feed! id))))

(defn select-group!
  "Into a group: every article of every feed that carries this name, in one
  list. A sibling of `All feeds` and not a drawer that opens in the sidebar
  - it is a place to read, with its own address, so the back button leads
  out of it like it leads out of a feed.

  Which feeds those are is the server's answer (`leser.feeds/of-group`), so
  nothing is merged here. The feed is cleared, because a group is the wider
  of the two lists and a window in both would be in neither."
  [group]
  (go! (route/href (merge (select-keys @!state [:filter :feed-filter])
                          {:group group})))
  (vt/transition! :forward :list
                  #(do (swap! !state assoc :feed nil :group group :article nil
                              :articles [] :pane :articles)
                       (load-articles!))))

(defn home!
  "To the front: every feed, nothing open, the list.

  The one move that does not depend on where it is made from - out of an
  article, out of the settings, out of a feed, all of it in one press - which
  is why the button is in the same place on every screen (`leg.nav`). A place,
  so it is pushed and the back button leads back to wherever it was pressed.

  `narrow?` because the front of a narrow window is the list of feeds and the
  front of a wide one is all three columns with the feeds beside them. The
  filter is left as it was: it is what somebody reads with, not where they
  are."
  [narrow?]
  (let [pane (if narrow? :feeds :articles)]
    (go! (route/href (merge (select-keys @!state [:filter :feed-filter])
                            {:pane pane})))
    (vt/transition! :back :all #(do (show-feed! nil)
                                    (swap! !state assoc :page nil :pane pane
                                           :dialog nil)))))

(defn set-filter! [f]
  (swap! !state assoc :filter f :articles [])
  (stay! (here (:article/id (:article @!state))))
  (load-articles!))

(defn set-feed-filter!
  "Which names are listed: all of them, or the ones with something left in
  them. Nothing is fetched - every feed is already here with its count, and
  this only decides which of them are drawn."
  [f]
  (swap! !state assoc :feed-filter f)
  (stay! (here (:article/id (:article @!state)))))

;;; -------------------------------------------------------------- articles

(defn- patch-row [articles id f]
  (mapv #(if (= id (:article/id %)) (f %) %) articles))

(defn- sort-key
  "How the server orders a list: published, or when it was first seen if the
  feed gave no date. Written out here because a row that is put into the
  list by hand has to land where the server would have put it."
  [a]
  (or (:article/published a) (:article/first-seen a) ""))

(defn- with-row
  "The list, with this article in it - where its date says it belongs, and
  only if it is not there already.

  A window opened straight onto an article has a list that was fetched
  without it: the unread filter does not list what has already been read,
  and the article somebody reloaded on has been. Without this, stepping
  left or right out of it would have nowhere to go, because the list this
  window can see would not contain the thing it is showing.

  Which way the list runs decides where `before` is, so the setting is
  read here too - a row dropped into a list read forwards at the place a
  list read backwards would have put it is a row in the wrong half of it,
  and stepping out of the article would then step the wrong way.

  The body comes off: a row is a row, and `leg.articles` renders the
  teaser."
  [articles article oldest-first?]
  (let [id  (:article/id article)
        row (dissoc article :article/body)]
    (if (some #(= id (:article/id %)) articles)
      articles
      (let [k        (sort-key row)
            before?  (if oldest-first?
                       #(not (pos? (compare (sort-key %) k)))
                       #(not (neg? (compare (sort-key %) k))))
            [before after] (split-with before? articles)]
        (vec (concat before [row] after))))))


(defn read-box
  "The box an open article scrolls in, if it is on the screen at all. Also
  the answer to \"is somebody reading\": a narrow window showing its list
  has no such box, even while an article is open behind it.

  Asked of the document rather than held in a ref, because the thing that
  wants it is never the component that drew it - the keys (`leg.keys`), and
  the step from one article to the next below."
  []
  (.querySelector js/document "[data-keys=\"read\"]"))

(defn- index-of
  "Where along the list the article with this id is, or nil when the list
  does not hold it."
  [articles id]
  (first (keep-indexed (fn [i a] (when (= id (:article/id a)) i)) articles)))

(defn- sibling
  "The article `step` places along the list from the one that is open, or
  nil at either end. The list in the window and not the server's: what
  somebody is stepping through is what they can see, in the order they are
  looking at it.

  `from` is which article to count from, and is the open one unless it is
  said: the page stuck to the edge of the one being read draws its own
  toolbar, and an arrow in it that said there was nothing that way when
  there was would be wrong for the one frame in which it is swapped for the
  real page (`leg.article/Peek`)."
  ([state step] (sibling state step (:article/id (:article state))))
  ([{:keys [articles]} step from]
   (let [i (index-of articles from)
         j (when i (+ i step))]
     (when (and j (<= 0 j) (< j (count articles)))
       (:article/id (nth articles j))))))


;;; ------------------------------------------------------------- look-ahead

(def ^:private warm-images
  "How many pictures out of an article ahead are asked for. The ones that
  will be on the screen when it arrives and not the forty below them that
  nobody may ever scroll to: a look-ahead that pulled a whole article's
  worth of pictures over a phone's connection would cost more than the
  blink it saves."
  4)

(defn- image-sources
  "The address of every picture in a piece of markup, in the order they
  stand in it.

  Matched rather than parsed, which is the wrong way round everywhere else
  in this project and is the right way round here: what is read is a
  document this project's own parser has already written out
  (`leser.html/sanitize`), with every attribute in double quotes and every
  double quote inside one escaped, so there is nothing left for a parser to
  disagree with. And the answer is only ever used to ask for a file - the
  worst a wrong one can do is fetch something nobody needed."
  [html]
  (when html
    (map second (re-seq #"<img[^>]*\ssrc=\"([^\"]*)\"" html))))

(defn- warm!
  "The pictures of an article ahead, fetched while it is still off the
  screen.

  The body arrives here as markup and the browser does not ask for a
  picture in it until that markup is in the document - which is after the
  slide has begun, so the picture lands in a page that has already arrived
  and the article is drawn twice. An `Image` with nothing but a `src` asks
  the browser for exactly the file the page will ask for a moment later,
  and the second ask is answered out of the cache.

  Which also goes around the `loading=\"lazy\"` the server writes on every
  one of them, deliberately: lazy is right for an article being read and
  wrong for an article being made ready."
  [{:keys [article]}]
  (when (exists? js/Image)
    (doseq [src (take warm-images (image-sources (:article/body article)))]
      (set! (.-src (js/Image.)) src))))

(defonce ^:private !ahead
  ;; What has been asked for and what has come back, by article id:
  ;; `{:asked promise, :answer the server's answer once it is here}`.
  ;;
  ;; The promise and not only the answer, because a finger can arrive while
  ;; the look-ahead is still in flight and that swipe is to wait for the
  ;; request that is already running rather than start a second one.
  (atom {}))

(defn- patch-ahead!
  "`f` applied to the copy of this article the look-ahead put by, where it
  has one.

  The look-ahead keeps whole answers from the server, every one of them
  fetched before anybody had read it, and a step back onto one is served
  out of here rather than asked for again. So a mark made in this window
  has to reach this copy too: otherwise stepping away and back brings the
  old mark with it, and the dot in the toolbar says unread about something
  the list has already greyed out."
  [id f]
  (swap! !ahead (fn [m] (if (get-in m [id :answer :article])
                          (update-in m [id :answer :article] f)
                          m))))

(defn- patch-article!
  "`f` applied to this article wherever this window is holding it: its row
  in the list, the copy that is open, and the one the look-ahead put by.

  One mark in three places, because there are three copies of every article
  in here and a reader can see two of them at once. The row is what greys
  out, the open copy is what the toolbar's dot is drawn from
  (`leg.article/Toolbar`), and the look-ahead's is what the next step back
  will show. A window that marked one of them is a window arguing with
  itself."
  [id f]
  (swap! !state (fn [s] (-> s
                            (update :articles patch-row id f)
                            (update :article #(if (and % (= id (:article/id %))) (f %) %)))))
  (patch-ahead! id f))

(defn read-now
  "`a`, read. A mark it already carries is left alone: that one came from
  the server and is the older of the two, which is the one worth keeping -
  and keeping it is what makes this safe to apply twice to the same
  article, which `load-article!` does."
  [a]
  (cond-> a (not (:article/read a)) (assoc :article/read (now-iso))))

(defn- have?
  "Whether this article is here already, rather than on its way."
  [id]
  (some? (:answer (get @!ahead id))))

(defn- ask-for
  "The article, from the look-ahead where it is and from the server where
  it is not. The one way anything in this file asks for an article, so
  neither the caller nor the look-ahead has to know which it got."
  [id]
  (or (get-in @!ahead [id :asked])
      (let [p (-> (api/article! id)
                  (.then (fn [answer]
                           ;; Only if it is still wanted: an answer to a
                           ;; question that has since been forgotten
                           ;; (`look-ahead!`) must not put it back.
                           (swap! !ahead (fn [m]
                                           (cond-> m
                                             (contains? m id)
                                             (update id assoc :answer answer))))
                           answer)))]
        (swap! !ahead update id assoc :asked p)
        ;; A fetch that failed is not an answer and is not kept as one - and
        ;; this is also what makes a look-ahead nobody waited for a handled
        ;; rejection rather than a noise in the console.
        (.catch p (fn [_] (swap! !ahead dissoc id) nil))
        p)))

(defn- look-ahead!
  "Fetch the article on either side of the one that is open, and forget
  everything that is no longer next to it.

  One each way and not three: a step is almost always the next one or the
  one before, the second step is made when the first has arrived and moved
  the window along, and an article is a document with pictures in it - a
  reader that pulled six of them down a phone's connection to save a
  quarter of a second would be spending somebody else's money on it.

  Forgetting is the same call, because what to keep is exactly what to
  fetch: everything else is an article the window has already walked past."
  []
  (let [s    @!state
        here (:article/id (:article s))
        near (keep #(sibling s %) [-1 1])]
    (swap! !ahead select-keys (cons here near))
    (doseq [id near]
      (-> (ask-for id) (.then warm!) (.catch (fn [_] nil))))))


(defn load-article!
  "Fetch one article and mark it read, without touching the address. What
  the back button reaches, and what everything below it is built on.

  Read here first and told to the server after: the row greys out under the
  press rather than two hundred milliseconds later. It stays in the list
  even under the unread filter until the list is fetched again - an article
  that vanished the moment it was opened would take its own back button
  with it."
  ([id] (load-article! id nil))
  ([id direction]
   ;; The one that is open is left where it is until the next one is here:
   ;; blanking the column first would make stepping through a feed a blink
   ;; of nothing between two articles, and would give the view transition
   ;; an empty page to cross-fade to (`leg.vt`). The bar under the toolbar
   ;; is what says something is on its way - and only where something is:
   ;; an article the look-ahead already has is here before this frame is
   ;; drawn, and a bar that came and went in the same breath would be a
   ;; flicker where the point of the look-ahead was to take one away.
   (swap! !state assoc :pane :article :article-loading? (not (have? id)))
   ;; Read, in all three places this article is held (`patch-article!`),
   ;; and on the answer below as it comes in. The fetch and the telling go
   ;; to the server side by side and the fetch usually wins, so what comes
   ;; back is an article that was unread a moment ago: drawing it would put
   ;; a hollow dot in the toolbar over a row that has just gone grey - and
   ;; which of the two the reader believed would be whichever they looked
   ;; at.
   (patch-article! id read-now)
   (-> (ask-for id)
       (.then (fn [{:keys [article]}]
                (let [article (read-now article)]
                  (vt/transition!
                   direction :read
                   ;; Where the box is put is not decided here: the article
                   ;; is opened at the line it was last left at, and that is
                   ;; a layout effect in the component that drew the box
                   ;; (`leg.article/ArticlePane`, `leg.scroll`) - which React
                   ;; runs inside this very callback, after the swap below
                   ;; and before the browser's first picture.
                   #(do (swap! !state (fn [s] (-> s
                                                  (assoc :article article
                                                         :article-loading? false)
                                                  (update :articles with-row article
                                                          (u/oldest-first?
                                                           (:reader/order (:settings s)))))))
                        ;; And then the two beside this one, so the next step
                        ;; is as quick as this one just was. In here and not
                        ;; after the call: `startViewTransition` runs what it
                        ;; is given at the next rendering opportunity, so out
                        ;; there the article that is open would still be the
                        ;; one this just stepped off, and the look-ahead
                        ;; would be a step behind for good.
                        (look-ahead!))))))
       (.catch (fn [e] (article-done!) (vt/settle!) (fail! e))))
   (-> (api/mark-read! [id] true)
       (.then feeds!)
       (.catch fail!))))

(defn open-article!
  "Into an article: a place, so it is pushed and the back button leads out
  of it and onto the list it was opened from."
  [id]
  (go! (here id))
  (load-article! id :forward))

(defn step-article!
  "One along the list, without coming back out to it. `1` is the next
  article, `-1` the one before.

  The address is replaced rather than pushed: reading through twenty
  articles should not put twenty entries behind the back button, which
  still means \"back to the list\". The address does name the one being
  read, so a reload or a shared link lands on it.

  Nothing happens at either end. A reader that wrapped around would tell
  somebody they were at the top when they were at the bottom, and a reader
  that jumped into the next feed would be answering a question nobody
  asked - so what a finger pushed aside is put back instead (`vt/settle!`),
  which is the only answer there is to a question about an article that is
  not there.

  The page starts moving before the answer is in. `vt/glide!` carries it on
  in the direction it was already going while the article is fetched, and
  the transition in `load-article!` picks it up from wherever it got to: one
  movement from the finger to the next page, rather than a drag, a pause and
  an animation.

  Usually there is no answer to wait for: the article on either side was
  fetched when this one arrived (`look-ahead!`), so the glide is a frame or
  two and the slide begins where the finger stopped. The glide is still
  what happens when it is not - a feed that is slow, a connection that is
  gone - and that is the only case it was ever for."
  [step]
  (let [s @!state
        direction (if (neg? step) :back :forward)]
    (if-let [id (sibling s step)]
      (do (stay! (here id))
          ;; And only where there is something to wait for, which is now
          ;; exactly where there is nothing at the edge to move into: the
          ;; page beside this one is drawn only once its answer is in
          ;; (`use-neighbours`), so `have?` is both questions at once.
          ;; With the answer in hand the page is held where the finger left
          ;; it and the transition takes it from there, two pages edge to
          ;; edge. Without it there is no neighbour to show, and a glide
          ;; into the paper behind the window is still better than a page
          ;; that stopped dead and jumped again when the answer came.
          (when-not (have? id) (vt/glide! direction))
          (load-article! id direction))
      (vt/settle!))))

(defn next-article! [] (step-article! 1))
(defn previous-article! [] (step-article! -1))

(defn has-sibling?
  "Whether there is one that way. For a button that should say so rather
  than do nothing when it is pressed. `from` as in `sibling`: the open
  article unless another one is named."
  ([state step] (some? (sibling state step)))
  ([state step from] (some? (sibling state step from))))

(defn position
  "How far along the list the open article is, as `[nth of]`, counted from
  one - or nil when it is not in the list at all.

  The window's list and not the server's, the same one the arrows step
  along: what the number says is where somebody is in what they can see,
  which is filtered and cut at `limit`. A total out of the server would be
  a larger number than the arrows can reach."
  [{:keys [articles article] :as _state}]
  (when-let [i (index-of articles (:article/id article))]
    [(inc i) (count articles)]))

(defn drag-article!
  "The page, under the finger, `dx` pixels from where the touch went down.
  Called for every frame of a sideways drag (`leg.swipe`).

  Dragging left asks for the next article, so what is checked is whether
  there is one: at the end of the list the page still moves, a quarter as
  far, and springs back when the finger comes off. A page that would not
  budge is a page that looks stuck; a page that gives a little and comes
  back says there is nothing there, which is the truth."
  [dx]
  (let [far? (has-sibling? @!state (if (neg? dx) 1 -1))]
    (vt/drag! (if far? dx (/ dx 4)))))

(defn settle-article!
  "Nothing came of the drag: the page goes back where it was."
  []
  (vt/settle!))

(defn close-article!
  "Out of the article and back to the list.

  The browser's own back where this window walked in, so the button in the
  corner and the one in the browser do the same thing rather than two
  things that look the same. A window that was opened on the article has
  nothing behind it, and gets its address rewritten instead."
  []
  (if (pos? (depth))
    (.back js/history)
    (do (vt/transition! :back :read #(swap! !state assoc :article nil :pane :articles))
        (stay! (here)))))

(defn back!
  "One step back the way this window came in, whatever that was: out of the
  article, out of a page, out of the pane. The browser's own history, because
  that is what every step in here wrote (`go!`) and the phone's back gesture
  already walks it - one way back, not a second one that disagrees with it.

  A window that was opened *on* an article or a page has nothing behind it
  and the history would leave the reader altogether, so those two close
  themselves instead; they already know how (see `close-article!`)."
  []
  (let [{:keys [page article]} @!state]
    (cond
      (pos? (depth)) (.back js/history)
      page           (close-page!)
      article        (close-article!)
      :else          nil)))

(defn set-read! [id read?]
  (let [stamp (now-iso)]
    (patch-article! id #(if read?
                          (assoc % :article/read stamp)
                          (dissoc % :article/read))))
  (-> (api/mark-read! [id] read?) (.then feeds!) (.catch fail!)))

(defn unread-down-to
  "The ids of everything in `articles` from the top of the list down to
  `id`, that row included, that has not been read yet.

  Pure, and the whole of what \"up to here\" means - which is why it is a
  function and not a line inside the one below: the label on the press says
  how many rows it would change, so the number somebody reads and the marks
  that are made come out of the same count.

  From the top of the list, and not from the last thing read: a list read
  out of order has holes in it, and the row somebody presses is the line
  between what they are done with and what is left - everything above it
  belongs on the done side, whichever order they got to it in. Empty where
  there is nothing left to mark, and empty where the id is not in the list
  at all: a row that is no longer there is not a reason to mark everything."
  [articles id]
  (let [[above [row]] (split-with #(not= id (:article/id %)) articles)]
    (if row
      (into [] (comp (remove :article/read) (map :article/id))
            (conj (vec above) row))
      [])))

(defn read-down-to!
  "Everything down to this row, read. What a long press on a row offers
  (`leg.articles/Row`).

  The rows go grey where they are rather than leaving the list, like every
  other mark in here: a list is fetched again when somebody asks for it,
  not under the thumb that just pressed it. The counts come back from the
  server, which is the only place they are ever worked out.

  One request for all of them, because it is one press: `mark-read!` takes
  the ids together, so a hundred rows cost one round trip and the sidebar
  changes once."
  [id]
  (let [ids (unread-down-to (:articles @!state) id)]
    (when (seq ids)
      (let [stamp (now-iso)
            marked (set ids)]
        (swap! !state update :articles
               (fn [as] (mapv #(if (marked (:article/id %))
                                 (assoc % :article/read stamp)
                                 %)
                              as)))
        ;; And the one that is open, if it was among them - it is drawn from
        ;; its own copy, which would otherwise go on saying unread.
        (swap! !state update :article
               (fn [a] (if (and a (marked (:article/id a)))
                         (assoc a :article/read stamp)
                         a)))
        ;; And the copies next door that the look-ahead is holding, for the
        ;; same reason one step later: a step onto one of them is served
        ;; out of there and not asked for again.
        (doseq [id ids] (patch-ahead! id #(assoc % :article/read stamp))))
      (-> (api/mark-read! ids true) (.then feeds!) (.catch fail!)))))

(defn set-starred! [id starred?]
  (let [stamp (now-iso)]
    (patch-article! id #(if starred?
                          (assoc % :article/starred stamp)
                          (dissoc % :article/starred))))
  (-> (api/star! [id] starred?)
      (.catch fail!)
      (.then (fn [_] (when (= :starred (:filter @!state)) (load-articles!))))))

(defn mark-feed-read!
  "Everything in the feed that is open - or everything there is, when that
  is all of them - and then out to the front. What somebody presses when a
  feed has got away from them.

  `older-than` is an age in seconds and nil is the whole feed: the question
  is asked in a dialog (`leg.dialogs/MarkRead`), because \"mark read\" on its
  own never said which ones and the answer somebody wants is usually not
  all of them.

  Out, because the press means done with this one: under the unread filter
  the list it was pressed on is empty the moment it answers, and an empty
  list is not a place to leave somebody standing. The same move as [Home]
  and not a smaller one, so that clearing a feed ends where every other way
  out of a feed ends. It is also what closes the dialog - `home!` clears it
  like every other move does.

  The counts are asked for again rather than taken from the answers: every
  mark answers the listing, but the marks are made at the same time, so the
  listing that came back last is not necessarily the one with all of them
  in it."
  [narrow? older-than]
  (let [{:keys [feed group feeds]} @!state
        ids (cond
              feed  [feed]
              ;; A group is marked read feed by feed, because that is what a
              ;; group is: there is no third thing to mark, only the feeds
              ;; carrying the name.
              group (keep #(when (= group (:feed/group %)) (:feed/id %)) feeds)
              :else (map :feed/id feeds))]
    (swap! !state assoc :busy? true)
    (-> (js/Promise.all (clj->js (map #(api/read-feed! % older-than) ids)))
        (.then (fn [_]
                 (swap! !state assoc :busy? false)
                 (home! narrow?)
                 (poll!)))
        (.catch fail!))))

;;; ---------------------------------------------------------------- feeds

(defn refresh!
  "Fetch, now. Answers when the fetching is over, because whoever pressed
  it is waiting to see the number change.

  `:refreshing?` beside `:busy?`, and not instead of it: `:busy?` is every
  press the window is waiting on - marking a span read, adding a feed,
  importing a file - and it is what disables a row of buttons that must not
  be pressed twice. This one says which of them is the press being waited
  on, so the word that was pressed can be the thing that moves
  (`leg.nav/Bar`, `aria-busy`). A [Refresh] breathing while somebody's
  [Mark read] runs would be the window pointing at the wrong button.

  It is put down one step later than `:busy?` as well. The fetching is over
  when the feeds answer, but what was asked for is a list with the new
  articles in it, and that arrives with `load-articles!` - so the word goes
  on moving until there is something to see."
  []
  (swap! !state assoc :busy? true :refreshing? true :error nil)
  (let [feed (:feed @!state)]
    (-> (if feed (api/refresh-feed! feed) (api/refresh-all!))
        (.then feeds!)
        (.then (fn [_] (swap! !state assoc :busy? false)))
        (.then load-articles!)
        (.then (fn [_] (swap! !state assoc :refreshing? false)))
        (.catch fail!))))

(defn add-feed! [url]
  (swap! !state assoc :busy? true :error nil)
  (-> (api/add-feed! url)
      (.then feeds!)
      (.then (fn [answer]
               ;; And out of the settings page, where the field is: what
               ;; was asked for is the feed, so the feed is what this ends
               ;; on.
               (swap! !state assoc :busy? false :dialog nil :page nil)
               (select-feed! (get-in answer [:feed :feed/id]))))
      (.catch fail!)))

(defn import-opml! [text]
  (swap! !state assoc :busy? true :error nil)
  (-> (api/import-opml! text)
      (.then (fn [answer]
               (swap! !state assoc :busy? false :dialog nil)
               (load-state!)
               answer))
      (.catch fail!)))

(defn export-opml!
  "Every subscription as a file, saved the way a browser saves a file: an
  anchor pointing at a blob, clicked by the program that made it. There is
  no download API and this is what one looks like.

  The object URL is given back straight away rather than at some point when
  the tab is closed - the file is already in the download folder by then,
  and a blob of every feed somebody has is nothing to keep alive for the
  rest of the session."
  []
  (swap! !state assoc :busy? true :error nil)
  (-> (api/export-opml!)
      (.then (fn [{:keys [opml]}]
               (swap! !state assoc :busy? false)
               (let [blob (js/Blob. #js [opml] #js {:type "text/x-opml"})
                     url  (.createObjectURL js/URL blob)
                     a    (.createElement js/document "a")]
                 (set! (.-href a) url)
                 (set! (.-download a) "leser-feeds.opml")
                 (.click a)
                 (.revokeObjectURL js/URL url)
                 true)))
      (.catch fail!)))

(defn remove-feed! [id]
  (swap! !state assoc :busy? true :error nil)
  (-> (api/remove-feed! id)
      (.then feeds!)
      (.then (fn [_]
               (swap! !state assoc :busy? false)
               (when (= id (:feed @!state))
                 ;; The feed that was open is gone, so the window falls back
                 ;; to every feed at once. Removed from the settings page,
                 ;; that is not a move to anywhere: the address belongs to
                 ;; the page, and writing the list's into it would leave
                 ;; somebody on a page the address bar no longer names.
                 (if (:page @!state)
                   (show-feed! nil)
                   (select-feed! nil)))))
      (.catch fail!)))

(defn remove-all-feeds!
  "Every subscription, gone. The dialog is what asked (see
  `leg.dialogs/RemoveAllFeeds`); this only sends it and closes the dialog on
  the answer, so a window that failed to reach the server is still standing
  on the question.

  The window falls back the same way one removal does - there is no feed
  left to be open in - and for the same reason it does not push an address:
  this is done on the settings page, and a place the page is not is not
  somewhere to leave somebody."
  []
  (swap! !state assoc :busy? true :error nil)
  (-> (api/remove-all-feeds!)
      (.then feeds!)
      (.then (fn [_]
               (swap! !state assoc :busy? false :dialog nil)
               (if (:page @!state)
                 (show-feed! nil)
                 (select-feed! nil))))
      (.catch fail!)))

(defn save-feed!
  "What this person has made of the feed: the name they call it and the
  group they have put it in. Both are sent as an empty string rather than
  left out, because empty is an answer - it hands the feed back its own
  name, and takes it out of every group.

  One call for the two because they are one dialog and one press (see
  `leg.dialogs/Feed`). Nothing here knows whether the group is new: typing
  a name that nothing carries yet is how a group is made."
  [id {:keys [title group]}]
  (-> (api/update-feed! id {:feed/title title :feed/group group})
      (.then feeds!)
      (.catch fail!)))

;;; -------------------------------------------------------------- settings

(defn save-settings!
  "One setting, saved on the press, and the answer is what the window then
  shows - the server sends back the settings it stored.

  Changing which way round the lists run fetches the list again, because
  the order is the server's and not a way of drawing what is already here:
  what a turned-round list holds is not what the old one held (see
  `leser.api/get-articles`). Nothing else on that page changes the list, so
  nothing else refetches it."
  [m]
  (-> (api/save-settings! m)
      (.then (fn [{:keys [settings]}]
               (let [turned? (not= (:reader/order (:settings @!state))
                                   (:reader/order settings))]
                 (swap! !state assoc :settings settings)
                 (when turned? (load-articles!)))))
      (.catch fail!)))

(defn load-readers!
  "Everybody who reads here. Only the admin may ask, and only the admin page
  does."
  []
  (swap! !state assoc :loading? true)
  (-> (api/admin-readers!)
      (.then (fn [{:keys [readers door]}]
               (swap! !state assoc :readers readers :door door :loading? false)))
      (.catch fail!)))

(defn add-reader!
  "Make somebody an account. Answers what happened, so the page can say
  whether that address was new - adding somebody twice is not an error and
  should not look like a success either."
  [email]
  (swap! !state assoc :busy? true :error nil)
  (-> (api/add-reader! email)
      (.then (fn [answer]
               (swap! !state assoc :busy? false :readers (:readers answer)
                      :door (:door answer))
               answer))
      (.catch fail!)))

(defn set-door!
  "Open the door for the next new address, or shut it again. One press, and
  what comes back is what it now is - the page shows the server's answer and
  never its own guess, because a switch that says open when the server never
  heard the press is the one lie this feature must not tell."
  [open?]
  (swap! !state assoc :busy? true :error nil)
  (-> (api/set-door! open?)
      (.then (fn [answer]
               (swap! !state assoc :busy? false :readers (:readers answer)
                      :door (:door answer))
               answer))
      (.catch fail!)))

(defn remove-client! [id]
  (-> (api/remove-client! id)
      (.then (fn [{:keys [settings]}] (swap! !state assoc :settings settings)))
      (.catch fail!)))

(defn sign-out! []
  (auth/sign-out!)
  (reset! !state (assoc @!state :feeds [] :articles [] :article nil :feed nil
                        :group nil :pane :articles :settings {} :dialog nil :error nil
                        :page nil :readers nil :door nil))
  (stay! (here)))

;;; ------------------------------------------------------------- the address

(defn apply-location!
  "What the address says, onto the window. This is what the back and
  forward buttons reach, so nothing in it writes an address - that would be
  a window arguing with its own history.

  The list is only fetched again when the address names another one.
  Stepping out of an article and back into it must not cost a request for
  twenty rows that have not changed."
  []
  (let [{:keys [feed group article page filter feed-filter pane]}
        (route/parse (.-href js/location))
        was @!state]
    (if page
      ;; Onto the page and nothing else: the list behind it is untouched,
      ;; because coming back out of it must not cost a fetch of rows that
      ;; have not changed.
      (do (vt/transition! :forward :all #(swap! !state assoc :page page :dialog nil))
          (when (and (= :admin page) (not (:readers was))) (load-readers!)))
      (do
        (swap! !state assoc :page nil :feed feed :group group :filter filter
               :feed-filter feed-filter :pane pane :dialog nil)
        (when (or (not= feed (:feed was)) (not= group (:group was))
                  (not= filter (:filter was)))
          (swap! !state assoc :articles [])
          (load-articles!))
        (cond
          (nil? article) (vt/transition! :back :read #(swap! !state assoc :article nil))
          (not= article (:article/id (:article was))) (load-article! article :back))))))

(defn install-routing!
  "The address this window was opened with, and every one the buttons go to
  afterwards. Called once, before the first fetch, so the first list asked
  for is the one the address names rather than the default one - the
  article it names is fetched by `load-state!`, which is the first point
  there is a key to fetch it with."
  []
  (.addEventListener js/window "popstate" (fn [_] (apply-location!)))
  (let [{:keys [feed group page filter feed-filter pane]}
        (route/parse (.-href js/location))]
    (swap! !state assoc :feed feed :group group :filter filter
           :feed-filter feed-filter :page page :pane pane)))

;;; ---------------------------------------------------------- the hook

(defn use-state [] (kc/use-atom !state))
(defn use-value [path] (kc/use-atom !state path))

(defn use-neighbours
  "The article before and the article after the one being read, bodies and
  all, as far as the look-ahead has them: `{:previous …, :next …}`, either
  of them nil.

  Bodies, because what these are for is being drawn. A page turn on a phone
  is two pages edge to edge, and the half of it the finger does needs the
  second page to already be at the edge - so the two the look-ahead
  fetched are drawn there (`leg.article/Peek`), and the drag pulls the real
  next article into view rather than the paper behind the window.

  Two atoms and not one: the list and what is open are the window's state,
  what has come back from the server is the look-ahead's. Reading them
  together is the one place that needs both, and nothing is copied from
  the second into the first - a neighbour cached under an id is already the
  answer to \"which article is next\" asked twice."
  []
  (let [state (kc/use-atom !state)
        ahead (kc/use-atom !ahead)
        at    (fn [step] (some-> (sibling state step) ahead :answer :article))]
    {:previous (at -1) :next (at 1)}))
